import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, LogOut, Calendar, User, Bold, Italic, AlignLeft, AlignCenter, AlignRight, AlignJustify, Image as ImageIcon, Type, Undo, Redo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = "https://effendialogue-2.onrender.com";
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    author: "",
    category: "Human VS Computer",
    excerpt: "",
    image_url: "",
    is_featured: false,
    created_at: ""
  });
  const [contentHistory, setContentHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const contentRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchPosts();
  }, []);

  useEffect(() => {
    if (activeTab === "subscribers") {
      fetchSubscribers();
    }
  }, [activeTab]);

  useEffect(() => {
  document.title = "Effendialogue";
}, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Auth verification failed:", error);
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      setSubscribersLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      toast.error("Failed to fetch subscribers");
    } finally {
      setSubscribersLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  const validateImageUrl = async (url) => {
    if (!url.trim()) return true; // Empty URL is okay
    
    const token = localStorage.getItem("admin_token");
    if (!token) return false;

    try {
      await axios.post(`${API}/validate-image-url`, { url }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      console.error("URL validation error:", error);
      toast.error("Invalid image URL");
      return false;
    }
  };

  // Rich text formatting functions
  const saveToHistory = (content) => {
    const newHistory = contentHistory.slice(0, historyIndex + 1);
    newHistory.push(content);
    setContentHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleContentChange = (newContent) => {
    setFormData({...formData, content: newContent});
    saveToHistory(newContent);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setFormData({...formData, content: contentHistory[newIndex]});
    }
  };

  const handleRedo = () => {
    if (historyIndex < contentHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setFormData({...formData, content: contentHistory[newIndex]});
    }
  };

  const insertFormatting = (openTag, closeTag) => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const beforeText = formData.content.substring(0, start);
    const afterText = formData.content.substring(end);

    const newContent = beforeText + openTag + selectedText + closeTag + afterText;
    handleContentChange(newContent);

    // Set cursor position after inserted tags
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, end + openTag.length);
    }, 0);
  };

const insertImage = () => {
  const imageUrl = prompt("Enter image URL:");
  if (imageUrl) {
    insertFormatting(
      `<div class="relative w-full max-w-4xl mx-auto mb-12 flex justify-center">
        <img
          src="${imageUrl}"
          alt="Image"
          class="w-full sm:w-3/4 lg:w-1/2 h-auto rounded-xl shadow-2xl object-cover"
        />
      </div>`,
      ''
    );
  }
};




  const applyFormatting = (format) => {
    switch(format) {
      case 'bold':
        insertFormatting('<strong>', '</strong>');
        break;
      case 'italic':
        insertFormatting('<em>', '</em>');
        break;
      case 'h1':
        insertFormatting('<h1 style="font-size: 2.5em; font-weight: bold; margin: 20px 0;">', '</h1>');
        break;
      case 'h2':
        insertFormatting('<h2 style="font-size: 2em; font-weight: bold; margin: 18px 0;">', '</h2>');
        break;
      case 'h3':
        insertFormatting('<h3 style="font-size: 1.5em; font-weight: bold; margin: 16px 0;">', '</h3>');
        break;
      case 'left':
        insertFormatting('<div style="text-align: left;">', '</div>');
        break;
      case 'center':
        insertFormatting('<div style="text-align: center;">', '</div>');
        break;
      case 'right':
        insertFormatting('<div style="text-align: right;">', '</div>');
        break;
      case 'justify':
        insertFormatting('<div style="text-align: justify;">', '</div>');
        break;
      case 'image':
        insertImage();
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    try {
      const submitData = { ...formData };
      
      // Convert created_at to ISO format if provided
      if (submitData.created_at) {
        submitData.created_at = new Date(submitData.created_at).toISOString();
      } else {
        delete submitData.created_at;
      }
      
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Post updated successfully!");
      } else {
        await axios.post(`${API}/posts`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Post created successfully!");
      }
      
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to save post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      author: post.author,
      category: post.category || "Human VS Computer",
      excerpt: post.excerpt || "",
      image_url: post.image_url || "",
      is_featured: post.is_featured || false,
      created_at: post.created_at ? new Date(post.created_at).toISOString().slice(0, 16) : ""
    });
    setIsCreateOpen(true);
  };

  const handleDelete = async (postId) => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    try {
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post deleted successfully!");
      fetchPosts();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete post");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      author: "",
      category: "Human VS Computer",
      excerpt: "",
      image_url: "",
      is_featured: false,
      created_at: ""
    });
    setEditingPost(null);
    setIsCreateOpen(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen">
        <div className="content-wrapper py-20">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded mb-8 w-1/3"></div>
            <div className="grid grid-cols-1 gap-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-gray-700 rounded-lg h-32"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      <div className="content-wrapper py-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl font-bold gradient-text mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-600">
              Manage your blog posts and content
            </p>
          </div>
          <div className="flex items-center gap-4">
            {activeTab === "posts" && (
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-post-button"
                    className="bg-yellow-500 hover:bg-yellow-600 text-black" 
                    onClick={() => resetForm()}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Post
                  </Button>
                </DialogTrigger>
              <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editingPost ? "Edit Post" : "Create New Post"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="title" className="text-white">Title</Label>
                    <Input
                      data-testid="post-title"
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="author" className="text-white">Author</Label>
                    <Input
                      data-testid="post-author"
                      id="author"
                      value={formData.author}
                      onChange={(e) => setFormData({...formData, author: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="category" className="text-white">Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger 
                        data-testid="post-category"
                        className="bg-gray-700 border-gray-600 text-white"
                      >
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 border-gray-600">
                        <SelectItem value="Human VS Computer" className="text-white hover:bg-gray-600">Human VS Computer</SelectItem>
                        <SelectItem value="World Around" className="text-white hover:bg-gray-600">World Around</SelectItem>
                        <SelectItem value="Being Human" className="text-white hover:bg-gray-600">Being Human</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="created_at" className="text-white">Publication Date (Optional)</Label>
                    <Input
                      data-testid="post-created-at"
                      id="created_at"
                      type="datetime-local"
                      value={formData.created_at}
                      onChange={(e) => setFormData({...formData, created_at: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                    <p className="text-xs text-gray-400">Leave empty to use current date/time</p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      data-testid="post-featured"
                      id="is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
                      className="border-gray-600"
                    />
                    <Label htmlFor="is_featured" className="text-white">
                      Set as Featured Article
                    </Label>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="excerpt" className="text-white">Excerpt (Optional)</Label>
                    <Textarea
                      data-testid="post-excerpt"
                      id="excerpt"
                      value={formData.excerpt}
                      onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="content" className="text-white">Content</Label>
                    
                    {/* Rich Text Toolbar */}
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-900 border border-gray-600 rounded-t-lg">
                      {/* Undo/Redo */}
                      <div className="flex gap-1 border-r border-gray-600 pr-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={handleUndo}
                          disabled={historyIndex <= 0}
                          className="text-white hover:bg-gray-700 disabled:opacity-50"
                          title="Undo"
                        >
                          <Undo className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={handleRedo}
                          disabled={historyIndex >= contentHistory.length - 1}
                          className="text-white hover:bg-gray-700 disabled:opacity-50"
                          title="Redo"
                        >
                          <Redo className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Font Size */}
                      <div className="flex gap-1 border-r border-gray-600 pr-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('h1')}
                          className="text-white hover:bg-gray-700"
                          title="Heading 1"
                        >
                          <Type className="h-4 w-4 mr-1" />H1
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('h2')}
                          className="text-white hover:bg-gray-700"
                          title="Heading 2"
                        >
                          <Type className="h-4 w-4 mr-1" />H2
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('h3')}
                          className="text-white hover:bg-gray-700"
                          title="Heading 3"
                        >
                          <Type className="h-4 w-4 mr-1" />H3
                        </Button>
                      </div>

                      {/* Text Style */}
                      <div className="flex gap-1 border-r border-gray-600 pr-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('bold')}
                          className="text-white hover:bg-gray-700"
                          title="Bold"
                        >
                          <Bold className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('italic')}
                          className="text-white hover:bg-gray-700"
                          title="Italic"
                        >
                          <Italic className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Alignment */}
                      <div className="flex gap-1 border-r border-gray-600 pr-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('left')}
                          className="text-white hover:bg-gray-700"
                          title="Align Left"
                        >
                          <AlignLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('center')}
                          className="text-white hover:bg-gray-700"
                          title="Align Center"
                        >
                          <AlignCenter className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('right')}
                          className="text-white hover:bg-gray-700"
                          title="Align Right"
                        >
                          <AlignRight className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => applyFormatting('justify')}
                          className="text-white hover:bg-gray-700"
                          title="Justify"
                        >
                          <AlignJustify className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Insert Image */}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => applyFormatting('image')}
                        className="text-white hover:bg-gray-700"
                        title="Insert Image"
                      >
                        <ImageIcon className="h-4 w-4 mr-1" />
                        Image
                      </Button>
                    </div>

                    <Textarea
                      ref={contentRef}
                      data-testid="post-content"
                      id="content"
                      value={formData.content}
                      onChange={(e) => handleContentChange(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white rounded-t-none"
                      rows={10}
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">Tip: Select text and click toolbar buttons to apply formatting. Use Ctrl+Z/Ctrl+Y for undo/redo</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="image_url" className="text-white">Image URL (Optional)</Label>
                    <Input
                      data-testid="post-image-url"
                      id="image_url"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                    {formData.image_url && (
                      <div className="mt-2">
                        <img 
                          src={formData.image_url} 
                          alt="Image preview" 
                          className="h-24 w-24 object-cover rounded border border-gray-600"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            toast.error("Failed to load image from URL");
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCreateOpen(false)}
                      className="border-gray-600 text-gray-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="save-post-button"
                      type="submit"
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      {editingPost ? "Update Post" : "Create Post"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
            
            <Button
              data-testid="logout-button"
              variant="outline"
              onClick={handleLogout}
              className="border-gray-600 text-gray-300"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 border-b border-gray-700">
          <button
            onClick={() => setActiveTab("posts")}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === "posts"
                ? "text-yellow-400 border-b-2 border-yellow-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Blog Posts ({posts.length})
          </button>
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === "subscribers"
                ? "text-yellow-400 border-b-2 border-yellow-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Newsletter Subscribers
          </button>
        </div>

        {/* Posts List */}
        {activeTab === "posts" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-white">
              Blog Posts ({posts.length})
            </h2>
          </div>
          
          {posts.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="py-12 text-center">
                <p className="text-gray-400 text-lg">No blog posts yet</p>
                <p className="text-gray-500">Create your first post to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {posts.map((post) => (
                <Card key={post.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          {post.image_url && (
                            <img 
                              src={post.image_url} 
                              alt={post.title}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <h3 
                              data-testid={`post-title-${post.id}`}
                              className="text-xl font-semibold text-white mb-1"
                            >
                              {post.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                              <div className="flex items-center gap-1">
                                <User className="h-4 w-4" />
                                <span>{post.author}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>{formatDate(post.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-300 line-clamp-2">
                          {post.excerpt || post.content.substring(0, 150) + "..."}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          data-testid={`edit-post-${post.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(post)}
                          className="border-gray-600 text-gray-300 hover:text-yellow-400 hover:border-yellow-400"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              data-testid={`delete-post-${post.id}`}
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-400 hover:text-red-300 hover:border-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-gray-800 border-gray-700">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete Post</AlertDialogTitle>
                              <AlertDialogDescription className="text-gray-300">
                                Are you sure you want to delete "{post.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-gray-600 text-gray-300">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(post.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Subscribers List */}
        {activeTab === "subscribers" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-white">
                Newsletter Subscribers ({subscribers.length})
              </h2>
              <Button
                onClick={fetchSubscribers}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                Refresh
              </Button>
            </div>

            {subscribersLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto"></div>
                <p className="text-gray-400 mt-4">Loading subscribers...</p>
              </div>
            ) : subscribers.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-400 text-lg">No subscribers yet</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4 text-gray-300 font-semibold">#</th>
                          <th className="text-left py-3 px-4 text-gray-300 font-semibold">Email</th>
                          <th className="text-left py-3 px-4 text-gray-300 font-semibold">Subscribed Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map((subscriber, index) => (
                          <tr key={index} className="border-b border-gray-700 hover:bg-gray-700/50">
                            <td className="py-3 px-4 text-gray-400">{index + 1}</td>
                            <td className="py-3 px-4 text-white">{subscriber.email}</td>
                            <td className="py-3 px-4 text-gray-400">
                              {subscriber.subscribed_at ? formatDate(subscriber.subscribed_at) : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;